import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Input } from 'src/components/UI/Forms/Inputs';
import { DEFAULT_EMPLOYEE, Employee } from 'src/utils/interfaces/interfaces';
import {
  useAddUserMutation,
  useGetAllEmployeeListQuery,
  useSearchEmployeeByNameQuery,
  useUpdateUserMutation,
} from 'src/services/transporterDrivers/transporterDrivers';
import {
  FullName,
  Email,
  SelectAnyValue,
  UaePhone,
  Required,
} from 'src/utils/validations/formValidations';
import PhoneInput from 'src/components/UI/Forms/Inputs/phoneInput';
import { useForm } from 'react-hook-form';
import DatePickerComponent from 'src/components/UI/Forms/Inputs/datePicker';
import { getUser } from 'src/utils/user/user';
import CustomSnackbar from 'src/components/UI/Alerts/customSnackbar';
import CustomSelect from 'src/components/UI/Forms/Inputs/customSelect';
import { Countries } from 'src/data/Countries';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@heroui/react';
import FileUpload from 'src/components/UI/Forms/Inputs/fileUpload';
import { EmployeeList } from 'src/components/Transporter/employeeList';
import EmployeeCard from 'src/components/Transporter/employeeCard';
import { FaPenToSquare, FaPlus } from 'src/icons';

export function Employees() {
  const [showEmployee, setShowEmployee] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const user = getUser();
  const location = useLocation();

  useEffect(() => {
    if ((location.state as any)?.openAdd) {
      setShowAddEmployee(true);
      // Clean up the history state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const [searchString, setSearchString] = useState('');
  const [idFile, setIdFile] = useState<File | null>(null);
  const [idExpiryDate, setIdExpiryDate] = useState<Date | null>(null);
  const [siraIdExpiryDate, setSiraIdExpiryDate] = useState<Date | null>(null);
  const [siraIdFile, setSiraIdFile] = useState<File | null>(null);
  const [isDateError, setIsDateError] = useState(false);
  const [isSiraDateError, setIsSiraDateError] = useState(false);
  const [selectedEmployee, setSelectedEmployee] =
    useState<Employee>(DEFAULT_EMPLOYEE);
  const [employeeFullData, setEmployeeFullData] = useState<any>([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [countryDialCode, setCountryDialCode] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success');
  const { data: employeeData, refetch: refetchEmployee, isFetching } = useGetAllEmployeeListQuery(
    { companyId: user?.company?._id, search: searchString },
    {
      skip: !user?.company?._id,
    }
  );
  const [addEmployee] = useAddUserMutation();
  const [updateEmployee] = useUpdateUserMutation();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm();
  const onChangeSearch = (searchString: any) => {
    setSearchString(searchString);
  };

  const siraIdValue = watch('siraId');

  useEffect(() => {
    if (employeeData?.response) {
      setEmployeeFullData(employeeData.response);
    } else {
      setEmployeeFullData([]);
    }
  }, [employeeData]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: string
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      switch (fileType) {
        case 'idFile':
          setIdFile(file);
          break;
        case 'siraIdFile':
          setSiraIdFile(file);
          break;
        default:
          console.warn('Unknown file type:', fileType);
      }
    }
  };

  const onAddEmployee = (data: any) => {
    if (!idExpiryDate) {
      setIsDateError(true);
      return;
    }

    if (data.siraId?.trim()) {
      if (!siraIdExpiryDate) {
        setIsSiraDateError(true);
        return;
      }
      setIsSiraDateError(false);
      data['siraIdExpiry'] = siraIdExpiryDate.toISOString().slice(0, 10);
      data['siraIdFile'] = siraIdFile;
    }

    setIsDateError(false);

    data['idExpiry'] = idExpiryDate.toISOString().slice(0, 10);
    data['idFile'] = idFile;
    data['designation'] = 'employee';
    let user = window.localStorage.getItem('user');
    if (user) {
      let companyDetails = JSON.parse(user);
      var companyId = companyDetails?.company?._id;
    }
    data['type'] = 'employee';
    data['company'] = companyId;
    data['phone'] = `+971${data.phone.replace(/^(?:\+?971)/, '')}`;
    data['status'] = 'active';
    data['reviewed'] = 'true';

    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });

    addEmployee(formData)
      .then((response: any) => {
        if (response.data) {
          setSnackbarMessage('Employee added successfully');
          setSnackbarSeverity('success');
          setOpenSnackbar(true);
          setShowAddEmployee(false);
          refetchEmployee();
          reset();
          setIdExpiryDate(null);
          setCountryDialCode('');
        } else if (response.error) {
          setSnackbarMessage(
            response.error.data.message
              ? response.error.data.message
              : 'Something went wrong.'
          );
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }
      })
      .catch((error: any) => {
        // Catch block for handling genuine errors
        setSnackbarMessage('Something went wrong.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setShowAddEmployee(false);
        refetchEmployee();
        reset();
        setIdExpiryDate(null);
      });
  };

  const onUpdateEmployee = (data: any) => {
    if (!idExpiryDate) {
      setIsDateError(true);
      return;
    }
    setIsDateError(false);

    data['isExpiry'] = idExpiryDate.toISOString().slice(0, 10);
    data['designation'] = 'employee';
    let user = window.localStorage.getItem('user');
    if (user) {
      let companyDetails = JSON.parse(user);
      var companyId = companyDetails?.company?._id;
    }
    data['type'] = 'employee';
    data['company'] = companyId;
    let _payload = data;
    _payload.phone = `+971${data.phone.replace(/^(?:\+?971)/, '')}`;
    updateEmployee({ data: _payload, id: selectedEmployee._id })
      .then((response: any) => {
        if (response.data) {
          setSnackbarMessage('Employee updated successfully');
          setSnackbarSeverity('success');
          setOpenSnackbar(true);
          setShowAddEmployee(false);
          refetchEmployee();
          reset();
          setIdExpiryDate(null);
          setCountryDialCode('');
          setShowEditEmployee(false);
        } else if (response.error) {
          setSnackbarMessage(
            response.error.data.message
              ? response.error.data.message
              : 'Something went wrong.'
          );
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }
      })
      .catch((error: any) => {
        // Catch block for handling genuine errors
        setSnackbarMessage('Something went wrong.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setShowAddEmployee(false);
        refetchEmployee();
        reset();
        setIdExpiryDate(null);
      });
  };

  const onEditEmployee = (data: any) => {
    setShowEditEmployee(true);
    setShowEmployee(false);

    reset({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone?.replace(/^(?:\+?971)/, '') || data.phone || '',
      email: data.email || '',
      idNumber: data.idNumber || '',
      siraId: data.siraId || '',
      nationality: data.nationality || '',
    });
  };

  useEffect(() => {
    if (selectedEmployee.idExpiry) {
      setIdExpiryDate(new Date(selectedEmployee.idExpiry));
    }
    if (selectedEmployee.siraIdExpiry) {
      setSiraIdExpiryDate(new Date(selectedEmployee.siraIdExpiry));
    }
    if (selectedEmployee.phone) {
      setCountryDialCode('+971');
    }
  }, [selectedEmployee]);

  const parsedExpiryDate = selectedEmployee.idExpiry
    ? new Date(selectedEmployee.idExpiry)
    : idExpiryDate;

  const { t } = useTranslation();

  return (
    <>
      <div className="w-full h-screen px-6 py-4 overflow-y-auto" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}>
        <CustomSnackbar
          open={openSnackbar}
          handleClose={() => setOpenSnackbar(false)}
          message={snackbarMessage}
          severity={snackbarSeverity}
          anchorOrigin={{ vertical: 'top', horizontal: 'end' }}
        />
        <div className="sticky top-0 z-10 py-6 pb-4" style={{ backgroundColor: 'var(--bg-base)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-2xl ms-4" style={{ color: 'var(--text-main)' }}>{t('nav_bar.employees')}</h3>
            <div className="flex justify-end gap-4">
              <Button
                variant="solid"
                size="sm"
                className="rounded-md shadow-lg m-1 p-5 font-semibold text-sm transition-colors duration-200"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--text-invert)',
                }}
                onPress={() => setShowAddEmployee(true)}
                startContent={<FaPlus />}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
              >
                {t('employee.add_employee')}
              </Button>
              <Input
                type="text"
                // disabled
                placeholder={t('employee.search_placeholder')}
                inputClassName="bg-[var(--bg-surface)] shadow-sm rounded-lg border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)] h-[45px]"
                onChange={(e: any) => onChangeSearch(e.target.value)}
                iconUrl="/assets/icons/search.svg"
              ></Input>
            </div>
          </div>
        </div>
        <section>
          {/* {filteredOrders &&
            filteredOrders.map((order: Order, count: number) => (
              <OrderCard
                order={order}
                key={count}
                onClick={() => navigate(`/orders/${order._id}`)}
              />
            ))} */}
        </section>
        <section>
          <div className="rounded-lg max-h-[70vh] overflow-y-auto">
            {employeeFullData && employeeFullData.length !== 0 ? (
              employeeFullData.map((user: Employee) => (
                <EmployeeList
                  key={user._id}
                  employee={user}
                  onAction={(employee: Employee) => {
                    setSelectedEmployee(employee);
                    setShowEmployee(true);
                  }}
                  onClose={() => setShowEmployee(false)}
                />
              ))
            ) : (
              <div className="px-6" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-main)' }}>
                <div className="sticky top-0 z-10 flex flex-col items-center py-8 pb-4 mt-48" style={{ backgroundColor: 'var(--bg-base)' }}>
                  <h3 className="col-span-2 mt-10 mb-2 text-2xl tracking-wide text-center" style={{ color: 'var(--text-main)' }}>
                    {t('employee.no_employee')}
                  </h3>
                  <div className="w-[100px] mt-2 h-1 bg-gradient-to-r from-transparent via-[var(--color-primary)] to-transparent"></div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      {/* ==================================================ADD EMPLOYEE=========================================== */}
      <Drawer
        isOpen={showAddEmployee}
        onClose={() => {
          setShowAddEmployee(false);
          reset();
        }}
        className="w-1/3"
        classNames={{
          closeButton:
            'm-2 p-2',
          header: 'px-6',
        }}
        style={{ backgroundColor: 'var(--bg-surface)' } as React.CSSProperties}
      >
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader className="flex flex-col gap-1 text-2xl" style={{ color: 'var(--text-main)' }}>
                {t('employee.add_employee')}
              </DrawerHeader>
              <DrawerBody>
                <form className="h-[80vh] overflow-y-scroll pe-5">
                  <Input
                    type="text"
                    name="firstName"
                    label={t('user.first_name')}
                    placeholder="Mohammed"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={FullName}
                    error={errors.firstName}
                    errorText={t('user.error_messages.first_name')}
                  />
                  <Input
                    type="text"
                    name="lastName"
                    label={t('user.last_name')}
                    placeholder="Ali"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={FullName}
                    error={errors.lastName}
                    errorText={t('user.error_messages.last_name')}
                  />
                  <PhoneInput
                    type="tel"
                    name="phone"
                    label={t('user.contact_number')}
                    placeholder="55XXXXXXX"
                    disableCountryCode={true}
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    prefixClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)] w-full"
                    register={register}
                    validation={UaePhone}
                    error={errors.phone}
                    errorText="Enter a valid 9-digit Mobile Number"
                    onCountryCodeChange={(selectedCode) => {
                      setCountryDialCode(selectedCode);
                    }}
                  />
                  <Input
                    type="email"
                    name="email"
                    label={t('user.email')}
                    placeholder="Enter employee email"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={Email}
                    error={errors.email}
                    errorText={t('user.error_messages.email')}
                  />
                  <Input
                    type="text"
                    name="idNumber"
                    label={t('employee.id_no')}
                    placeholder="Enter a valid ID Number"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={Required}
                    error={errors.idNumber}
                    errorText={t('user.error_messages.id_number')}
                  />

                  <FileUpload
                    id="idFile"
                    name="idFile"
                    label="ID Copy"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-dashed border-[var(--border-light)] rounded-lg text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    placeholder="Upload valid ID copy"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFileChange(e, 'idFile')
                    }
                    register={register}
                    validation={Required}
                    allowedFileTypes={[
                      'image/png',
                      'image/jpeg',
                      'application/pdf',
                    ]} // Allowed types
                    maxFileSize={1024 * 1024}
                    error={errors.idFile}
                    errorText="ID copy is required"
                  />

                  <DatePickerComponent
                    selectedDate={idExpiryDate}
                    onChange={setIdExpiryDate}
                    isDateError={isDateError}
                    minDate={new Date()}
                    label={t('employee.id_expiry')}
                    placeholder="MM/DD/YYYY"
                    errorText={t('user.error_messages.id_expiry')}
                    className="!mb-4"
                  />
                  <Input
                    type="text"
                    name="siraId"
                    label={t('employee.sira_id')}
                    placeholder="Enter a valid SIRA ID Number"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={{}}
                    error={errors.siraId}
                    errorText={t('user.error_messages.id_number')}
                    optional
                  />

                  {watch('siraId')?.trim() && (
                    <>
                      <FileUpload
                        id="siraIdFile"
                        name="siraIdFile"
                        label={t('employee.sira_id_copy')}
                        className="!mb-6"
                        inputClassName="shadow-sm border border-dashed border-[var(--border-light)] rounded-lg text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                        placeholder="Upload valid SIRA ID copy"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleFileChange(e, 'siraIdFile')
                        }
                        register={register}
                        validation={Required}
                        allowedFileTypes={[
                          'image/png',
                          'image/jpeg',
                          'application/pdf',
                        ]}
                        maxFileSize={1024 * 1024}
                        error={errors.siraIdFile}
                        errorText="SIRA ID copy is required"
                      />

                      <DatePickerComponent
                        selectedDate={siraIdExpiryDate}
                        onChange={setSiraIdExpiryDate}
                        isDateError={isSiraDateError}
                        minDate={new Date()}
                        label={t('employee.sira_id_expiry')}
                        placeholder="MM/DD/YYYY"
                        errorText={t('user.error_messages.id_expiry')}
                        className="!mb-4"
                      />
                    </>
                  )}
                  <CustomSelect
                    name="nationality"
                    label={t('user.nationality')}
                    labelClassName="!text-[var(--text-muted)] !text-xs !mb-1"
                    options={Countries}
                    placeholder={t('user.nationality')}
                    className="mb-5"
                    selectClassName="shadow-sm border border-[var(--border-light)] placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
                    register={register}
                    validation={SelectAnyValue}
                    setValue={setValue}
                    error={errors.nationality}
                    errorText={t('user.error_messages.nationality')}
                  ></CustomSelect>
                </form>
              </DrawerBody>
              <DrawerFooter className="m-auto">
                <Button
                  className="p-3 shadow-lg w-36 font-semibold transition-colors"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                  onClick={handleSubmit((d) => onAddEmployee(d))}
                >
                  {t('employee.add_employee')}
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
      <Drawer
        isOpen={showEditEmployee}
        onClose={() => {
          setShowEditEmployee(false);
          reset();
        }}
        className="w-1/3"
        classNames={{
          closeButton:
            'm-2 p-2',
          header: 'px-6',
        }}
        style={{ backgroundColor: 'var(--bg-surface)' } as React.CSSProperties}
      >
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader className="flex flex-col gap-1 text-2xl" style={{ color: 'var(--text-main)' }}>
                {t('employee.edit_employee')}
              </DrawerHeader>
              <DrawerBody>
                <form className="h-[80vh] overflow-y-scroll pe-5">
                  <Input
                    type="text"
                    name="firstName"
                    label={t('user.first_name')}
                    placeholder="Mohammed"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={FullName}
                    defaultValue={selectedEmployee?.firstName || ''}
                    error={errors.firstName}
                    errorText={t('user.error_messages.first_name')}
                  />
                  <Input
                    type="text"
                    name="lastName"
                    label={t('user.last_name')}
                    placeholder="Ali"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={FullName}
                    defaultValue={selectedEmployee?.lastName || ''}
                    error={errors.lastName}
                    errorText={t('user.error_messages.last_name')}
                  />
                  <PhoneInput
                    type="tel"
                    name="phone"
                    label={t('user.contact_number')}
                    placeholder="55XXXXXXX"
                    disableCountryCode={true}
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    prefixClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)] w-full"
                    register={register}
                    validation={UaePhone}
                    defaultValue={selectedEmployee?.phone?.replace(/^(?:\+?971)/, '') || ''}
                    error={errors.phone}
                    errorText="Enter a valid 9-digit Mobile Number"
                    onCountryCodeChange={(selectedCode) => {
                      setCountryDialCode(selectedCode);
                    }}
                  />
                  <Input
                    type="email"
                    name="email"
                    label={t('user.email')}
                    placeholder="Enter employee email"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={Email}
                    defaultValue={selectedEmployee?.email || ''}
                    error={errors.email}
                    errorText={t('user.error_messages.email')}
                  />
                  <Input
                    type="text"
                    name="idNumber"
                    label={t('employee.id_no')}
                    placeholder="Enter a valid ID Number"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={Required}
                    defaultValue={selectedEmployee?.idNumber || ''}
                    error={errors.idNumber}
                    errorText={t('user.error_messages.id_number')}
                  />

                  <DatePickerComponent
                    selectedDate={parsedExpiryDate}
                    onChange={(date: Date | null) => {
                      setIdExpiryDate(date);
                      setIsDateError(false);
                    }}
                    isDateError={isDateError}
                    minDate={new Date()}
                    label={t('employee.id_expiry')}
                    placeholder="MM/DD/YYYY"
                    errorText={t('user.error_messages.id_expiry')}
                    className="!mb-4"
                  />
                  <Input
                    type="text"
                    name="siraId"
                    label={t('employee.sira_id')}
                    placeholder="Enter a valid SIRA ID Number"
                    className="!mb-6"
                    inputClassName="shadow-sm border border-[var(--border-light)] text-[var(--text-main)] placeholder:text-[var(--text-muted)]"
                    register={register}
                    validation={{}}
                    defaultValue={selectedEmployee?.siraId || ''}
                    error={errors.siraId}
                    errorText={t('user.error_messages.id_number')}
                    optional
                  />

                  {watch('siraId')?.trim() && (
                    <>
                      <DatePickerComponent
                        selectedDate={selectedEmployee.siraIdExpiry ? new Date(selectedEmployee.siraIdExpiry) : siraIdExpiryDate}
                        onChange={(date: Date | null) => {
                          setSiraIdExpiryDate(date);
                          setIsSiraDateError(false);
                        }}
                        isDateError={isSiraDateError}
                        minDate={new Date()}
                        label={t('employee.sira_id_expiry')}
                        placeholder="MM/DD/YYYY"
                        errorText={t('user.error_messages.id_expiry')}
                        className="!mb-4"
                      />
                    </>
                  )}
                  <CustomSelect
                    name="nationality"
                    label={t('user.nationality')}
                    labelClassName="!text-[var(--text-muted)] !text-xs !mb-1"
                    options={Countries}
                    placeholder={t('user.nationality')}
                    className="mb-5"
                    selectClassName="shadow-sm border border-[var(--border-light)] placeholder:text-[var(--text-muted)] text-[var(--text-main)]"
                    register={register}
                    validation={SelectAnyValue}
                    setValue={setValue}
                    defaultValue={selectedEmployee?.nationality || ''}
                    error={errors.nationality}
                    errorText={t('user.error_messages.nationality')}
                  ></CustomSelect>
                </form>
              </DrawerBody>
              <DrawerFooter className="m-auto">
                <Button
                  className="p-3 shadow-lg w-36 font-semibold transition-colors"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                  onClick={handleSubmit((d) => onUpdateEmployee(d))}
                >
                  Save
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
      {/* ================================================================================================================== */}
      <Drawer
        isOpen={showEmployee}
        onClose={() => setShowEmployee(false)}
        className="w-[30%]"
        classNames={{
          closeButton:
            'm-2 p-2',
          header: 'px-6',
        }}
        style={{ backgroundColor: 'var(--bg-surface)' } as React.CSSProperties}
      >
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader className="flex flex-col gap-1 text-2xl" style={{ color: 'var(--text-main)' }}>
                {t('employee.employee_details')}
              </DrawerHeader>
              <DrawerBody>
                <div className="px-2 py-10" style={{ color: 'var(--text-main)' }}>
                  <EmployeeCard Employee={selectedEmployee} />
                </div>
              </DrawerBody>
              <DrawerFooter className="m-auto">
                {/* <Button color="danger" variant="solid" onPress={onClose}>
                  {t('button.close')}
                </Button> */}
                {/* <Button color="primary" onPress={onClose}>
                  Action
                </Button> */}
                <Button
                  variant="solid"
                  size="md"
                  className="w-full p-3 rounded-lg shadow-lg transition-colors duration-200"
                  style={{ backgroundColor: 'var(--bg-overlay)', border: '1px solid var(--border-light)', color: 'var(--text-main)' }}
                  onClick={() => onEditEmployee(selectedEmployee)}
                  startContent={<FaPenToSquare />}
                >
                  {t('button.edit')}
                </Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}
